// Design System Tokens - QResto Menu Viewer
// Based on systematic design principles for world-class UI

// Color Palette - Semantic naming with proper shades
export const colors = {
  // Primary - Warm Orange (brand color)
  primary: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316', // Main primary
    600: '#ea580c', // Current main color
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },
  
  // Secondary - Deep Gray (text & structure)
  secondary: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937', // Current dark text
    900: '#111827',
  },
  
  // Accent - Gold (highlights & bestsellers)
  accent: {
    50: '#fefce8',
    100: '#fef9c3',
    200: '#fef08a',
    300: '#fde047',
    400: '#facc15',
    500: '#eab308',
    600: '#f59e0b', // Current gold
    700: '#a16207',
    800: '#854d0e',
    900: '#713f12',
  },
  
  // Background colors
  background: {
    primary: '#ffffff',
    secondary: '#fef9f3', // Warm cream
    tertiary: '#f9fafb',
  },
  
  // Status colors
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  
  // Overlay
  overlay: {
    light: 'rgba(0, 0, 0, 0.3)',
    medium: 'rgba(0, 0, 0, 0.5)',
    dark: 'rgba(0, 0, 0, 0.7)',
  }
};

// Typography Scale - Using Major Third (1.25) ratio for harmonious scaling
export const typography = {
  // Font families
  fontFamily: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  // Font sizes (Major Third scale: 1.25 ratio)
  fontSize: {
    xs: '0.64rem',    // 10.24px
    sm: '0.8rem',     // 12.8px
    base: '1rem',     // 16px - base
    md: '1.25rem',    // 20px
    lg: '1.563rem',   // 25px
    xl: '1.953rem',   // 31.25px
    '2xl': '2.441rem', // 39px
    '3xl': '3.052rem', // 48.83px
    '4xl': '3.815rem', // 61.04px
  },
  
  // Font weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  // Line heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
  
  // Letter spacing
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
  }
};

// Spacing System - 8px grid (4, 8, 16, 24, 32, 48, 64, 96)
// Material UI uses 8px base, but also includes half-steps (4px)
export const spacing = {
  0: '0',
  0.5: '4px',   // 0.5 * 8
  1: '8px',     // 1 * 8
  1.5: '12px',  // 1.5 * 8
  2: '16px',    // 2 * 8
  2.5: '20px',  // 2.5 * 8
  3: '24px',    // 3 * 8
  3.5: '28px',  // 3.5 * 8
  4: '32px',    // 4 * 8
  5: '40px',    // 5 * 8
  6: '48px',    // 6 * 8
  7: '56px',    // 7 * 8
  8: '64px',    // 8 * 8
  10: '80px',   // 10 * 8
  12: '96px',   // 12 * 8
  16: '128px',  // 16 * 8
  20: '160px',  // 20 * 8
};

// Border Radius - Systematic scale
export const borderRadius = {
  none: '0',
  sm: '4px',
  base: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  full: '9999px',
};

// Shadows - Elevation system
export const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 2px 4px rgba(0, 0, 0, 0.06)',
  base: '0 4px 6px rgba(0, 0, 0, 0.07)',
  md: '0 6px 12px rgba(0, 0, 0, 0.08)',
  lg: '0 10px 20px rgba(0, 0, 0, 0.1)',
  xl: '0 16px 32px rgba(0, 0, 0, 0.12)',
  '2xl': '0 24px 48px rgba(0, 0, 0, 0.15)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
};

// Transitions - Timing functions
export const transitions = {
  duration: {
    fast: '150ms',
    base: '200ms',
    medium: '300ms',
    slow: '400ms',
  },
  timing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring effect
  }
};

// Z-index layers
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  backdrop: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
};

// Breakpoints - Mobile-first
export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px',
};

// Component-specific tokens
export const components = {
  card: {
    height: {
      small: '100px',
      medium: '120px',
      large: '140px',
    },
    imageSize: {
      small: '100px',
      medium: '120px',
      large: '140px',
    }
  },
  header: {
    height: {
      mobile: '240px',
      desktop: '280px',
    }
  },
  touchTarget: {
    minimum: '44px', // iOS Human Interface Guidelines
  }
};

const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  components,
};

export default designTokens;
